/**
 * 플랫폼 사용자 엑셀 일괄 등록 다이얼로그.
 * 엑셀 파싱·1차 검증 후 JSON 일괄 등록 API를 호출하고, 성공 시 임시 비밀번호를 1회 표시한다.
 */
import type { PlatformUserBulkRegisterResultUser } from '@/entities/user/api/user.api';
import { useBulkRegisterPlatformUsers } from '@/entities/user/hooks/usePlatformUsers';
import {
  downloadUserBulkRegisterResultXlsx,
  downloadUserBulkUploadTemplate,
  extractApiBulkRegisterErrorBody,
  mapServerBulkRegisterErrors,
  parseUserBulkUploadFile,
  USER_BULK_MAX_COUNT,
  type UserBulkParseResult,
  type UserBulkValidationError,
} from '@/pages/users/userBulkUploadUtils';
import { FileUploadButton } from '@/shared/components/FileUploadButton';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { popupPrimaryButtonClassName } from '@/shared/components/ui/popup-styles';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { Copy, Download, TriangleAlert } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { ToolbarRow } from '@/shared/components/ui/row';

type DialogPhase = 'upload' | 'preview' | 'success';

interface UserBulkRegisterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const UserBulkRegisterDialog: React.FC<UserBulkRegisterDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const toast = useToast();
  const bulkRegister = useBulkRegisterPlatformUsers();

  const [phase, setPhase] = useState<DialogPhase>('upload');
  const [fileName, setFileName] = useState('');
  const [parseResult, setParseResult] = useState<UserBulkParseResult | null>(
    null,
  );
  const [serverErrors, setServerErrors] = useState<UserBulkValidationError[]>(
    [],
  );
  const [resultUsers, setResultUsers] = useState<
    PlatformUserBulkRegisterResultUser[]
  >([]);

  const resetState = useCallback(() => {
    setPhase('upload');
    setFileName('');
    setParseResult(null);
    setServerErrors([]);
    setResultUsers([]);
  }, []);

  const handleOpenChange = (next: boolean) => {
    if (bulkRegister.isPending) return;
    onOpenChange(next);
    if (!next) resetState();
  };

  const validationErrors = [...(parseResult?.errors ?? []), ...serverErrors];

  const handleFileSelected = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;

    try {
      const result = await parseUserBulkUploadFile(file);
      setFileName(file.name);
      setParseResult(result);
      setServerErrors([]);
      setPhase('preview');
    } catch (err) {
      const message =
        err instanceof Error ? err.message : '엑셀 파일을 읽을 수 없습니다.';
      toast.error(message);
    }
  };

  const handleRegister = async () => {
    if (!parseResult || parseResult.errors.length > 0) return;

    setServerErrors([]);

    try {
      const response = await bulkRegister.mutateAsync({
        users: parseResult.users,
      });
      const createdCount = response.createdCount ?? response.users.length;
      setResultUsers(response.users);
      setPhase('success');
      toast.success(`${createdCount}명의 사용자를 등록했습니다.`);
    } catch (err) {
      const body = extractApiBulkRegisterErrorBody(err);
      if (body?.errors?.length) {
        setServerErrors(mapServerBulkRegisterErrors(body.errors));
        toast.error(body.message ?? '입력값을 확인해주세요.');
        return;
      }
      handleApiError(err, toast);
    }
  };

  const handleCopyPassword = async (password: string) => {
    try {
      await navigator.clipboard.writeText(password);
      toast.success('임시 비밀번호를 복사했습니다.');
    } catch {
      toast.error('복사에 실패했습니다.');
    }
  };

  const handleCopyRow = async (user: PlatformUserBulkRegisterResultUser) => {
    try {
      await navigator.clipboard.writeText(
        `${user.email}\t${user.temporaryPassword}`,
      );
      toast.success('계정 정보를 복사했습니다.');
    } catch {
      toast.error('복사에 실패했습니다.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {open ? (
        <DialogContent className="max-w-3xl">
          {phase === 'success' ? (
            <>
              <DialogHeader>
                <DialogTitle>사용자 일괄 등록 완료</DialogTitle>
                <DialogDescription>
                  {resultUsers.length}건이 등록되었습니다. 아래 임시 비밀번호는
                  이 화면에서만 확인할 수 있습니다.
                </DialogDescription>
              </DialogHeader>

              <div className="flex items-start gap-2 rounded-12 bg-ruda-error-50 px-3.5 py-2.5 text-sm text-ruda-error">
                <TriangleAlert
                  className="mt-0.5 h-4 w-4 shrink-0"
                  aria-hidden
                />
                <p className="min-w-0 flex-1">
                  창을 닫으면 임시 비밀번호를 다시 확인할 수 없습니다. 필요한
                  경우 결과 파일을 다운로드해 보관하세요.
                </p>
              </div>

              <div className="max-h-[320px] overflow-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>유저 ID</TableHead>
                      <TableHead>사용자 이메일</TableHead>
                      <TableHead>임시 비밀번호</TableHead>
                      <TableHead className="w-28">복사</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {resultUsers.map((user) => (
                      <TableRow key={user.userId}>
                        <TableCell>{user.userId}</TableCell>
                        <TableCell className="font-medium">
                          {user.email}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {user.temporaryPassword}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              aria-label={`${user.email} 임시 비밀번호 복사`}
                              onClick={() =>
                                void handleCopyPassword(user.temporaryPassword)
                              }
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              aria-label={`${user.email} 계정 정보 복사`}
                              onClick={() => void handleCopyRow(user)}
                            >
                              행
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <ToolbarRow gap={8}>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    downloadUserBulkRegisterResultXlsx(resultUsers)
                  }
                >
                  <Download className="mr-2 h-4 w-4" />
                  결과 다운로드
                </Button>
              </ToolbarRow>

              <DialogFooter layout="vertical">
                <Button
                  type="button"
                  className={popupPrimaryButtonClassName}
                  onClick={() => handleOpenChange(false)}
                >
                  확인 후 닫기
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>사용자 일괄 등록</DialogTitle>
                <DialogDescription>
                  엑셀 파일을 업로드해 플랫폼 사용자를 일괄 등록합니다. (최대{' '}
                  {USER_BULK_MAX_COUNT}건, 전체 성공 또는 전체 실패)
                  위탁계약서유형은 양식 콤보박스에서 내부/외부를 선택할 수
                  있으며, 비워 두면 내부(계약 필요)로 등록됩니다.
                </DialogDescription>
              </DialogHeader>

              <ToolbarRow gap={8}>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void downloadUserBulkUploadTemplate()}
                >
                  <Download className="mr-2 h-4 w-4" />
                  양식 다운로드
                </Button>
                <FileUploadButton
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  disabled={bulkRegister.isPending}
                  onFilesSelected={(files) => void handleFileSelected(files)}
                >
                  파일 선택
                </FileUploadButton>
              </ToolbarRow>

              {fileName ? (
                <p className="text-sm text-muted-foreground">
                  선택: {fileName}
                  {parseResult ? ` · ${parseResult.users.length}건` : ''}
                </p>
              ) : null}

              {validationErrors.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-ruda-error">
                    아래 오류를 수정한 뒤 다시 업로드해주세요.
                  </p>
                  <div className="max-h-[240px] overflow-auto rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-16">행</TableHead>
                          <TableHead className="w-40">항목</TableHead>
                          <TableHead>오류 내용</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {validationErrors.map((error, index) => (
                          <TableRow
                            key={`${error.row}-${error.field}-${index}`}
                          >
                            <TableCell>
                              {error.row > 0 ? error.row : '-'}
                            </TableCell>
                            <TableCell>{error.fieldLabel}</TableCell>
                            <TableCell className="text-ruda-error">
                              {error.message}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : null}

              {phase === 'preview' && validationErrors.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {parseResult?.users.length ?? 0}건 등록 준비됨
                </p>
              ) : null}

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  disabled={bulkRegister.isPending}
                  onClick={() => handleOpenChange(false)}
                >
                  취소
                </Button>
                <Button
                  type="button"
                  className="ruda-button"
                  disabled={
                    bulkRegister.isPending ||
                    !parseResult ||
                    parseResult.users.length === 0 ||
                    validationErrors.length > 0
                  }
                  onClick={() => void handleRegister()}
                >
                  {bulkRegister.isPending ? '등록 중...' : '등록'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      ) : null}
    </Dialog>
  );
};
